import { useEffect, useState } from "react";
import {
  ActivityIndicator,
  Image,
  ScrollView,
  Text,
  View,
  type ImageSourcePropType,
} from "react-native";
import { Check, Clock3, MapPin } from "lucide-react-native";
import {
  ActionBanner,
  Dialog,
  MoonSearchInput,
  MoonToggle,
  Row,
  colors,
} from "@louise/moon-ui";
import { useTranslations } from "@src/packages/i18n";
import { usePlacesPredictions } from "@src/packages/hooks/usePlacesPredictions";
import {
  extractCityAndCountry,
  getPlaceDetails,
  type PlacePrediction,
} from "@src/packages/clinics/utils/googlePlaces";
import { resolveTimezoneFromCoordinates } from "@src/packages/location/timezone";
import { GroupedCard } from "../components";
import { EMBER } from "../../../../onboarding/theme";
import { ICONS } from "../icons";
import type {
  UserPreferences,
  Language,
  DateFormat,
  YouScreenAction,
} from "../types";

const ILLUSTRATION_SIZE = 28;
const PICKER_MAX_HEIGHT = 420;
const PICKER_LIST_MAX_HEIGHT = 320;
const LOCATION_PICKER_MAX_HEIGHT = 520;
const LOCATION_PICKER_LIST_MAX_HEIGHT = 330;

const LANGUAGE_OPTION_KEYS: { labelKey: string; value: Language }[] = [
  { labelKey: "you.preferences.language_english", value: "en" },
  { labelKey: "you.preferences.language_french", value: "fr" },
];

const DATE_FORMAT_OPTIONS: { label: string; value: DateFormat }[] = [
  { label: "MM/DD/YYYY", value: "MM/DD/YYYY" },
  { label: "DD/MM/YYYY", value: "DD/MM/YYYY" },
  { label: "YYYY-MM-DD", value: "YYYY-MM-DD" },
];

const TIMEZONE_OPTION_KEYS: { labelKey: string; value: string }[] = [
  { labelKey: "you.preferences.tz_eastern", value: "America/New_York" },
  { labelKey: "you.preferences.tz_central", value: "America/Chicago" },
  { labelKey: "you.preferences.tz_mountain", value: "America/Denver" },
  { labelKey: "you.preferences.tz_pacific", value: "America/Los_Angeles" },
  { labelKey: "you.preferences.tz_alaska", value: "America/Anchorage" },
  { labelKey: "you.preferences.tz_hawaii", value: "Pacific/Honolulu" },
  { labelKey: "you.preferences.tz_london", value: "Europe/London" },
  { labelKey: "you.preferences.tz_paris", value: "Europe/Paris" },
  { labelKey: "you.preferences.tz_berlin", value: "Europe/Berlin" },
  { labelKey: "you.preferences.tz_tokyo", value: "Asia/Tokyo" },
  { labelKey: "you.preferences.tz_sydney", value: "Australia/Sydney" },
  { labelKey: "you.preferences.tz_utc", value: "UTC" },
];

type LocationSuggestion = {
  city: string;
  country: string;
  timezone: string;
};

// These provide a useful first view and a no-network fallback. Searching is
// still global through the established Places integration below.
const LOCATION_SUGGESTIONS: LocationSuggestion[] = [
  {
    city: "New York",
    country: "United States",
    timezone: "America/New_York",
  },
  {
    city: "Los Angeles",
    country: "United States",
    timezone: "America/Los_Angeles",
  },
  {
    city: "Chicago",
    country: "United States",
    timezone: "America/Chicago",
  },
  { city: "London", country: "United Kingdom", timezone: "Europe/London" },
  { city: "Paris", country: "France", timezone: "Europe/Paris" },
  { city: "Toronto", country: "Canada", timezone: "America/Toronto" },
  { city: "Tokyo", country: "Japan", timezone: "Asia/Tokyo" },
  { city: "Sydney", country: "Australia", timezone: "Australia/Sydney" },
];

const PREFERENCE_ICONS = {
  // A book is a clearer compact cue for language than the old connections
  // illustration. The remaining objects map directly to the setting they
  // control so the dialog does not fall back to generic decoration.
  language:
    require("../../../../assets/images/you-icons/learn.png") as ImageSourcePropType,
  dateFormat: ICONS.calendar,
  timezone: ICONS.mapPin,
  haptics: ICONS.sparkle,
  sound: ICONS.notifications,
};

type PickerId =
  "language" | "date-format" | "timezone-location" | "timezone-manual";

type PreferencesStatus = {
  isLoading: boolean;
  hasLoadError: boolean;
  hasSaveError: boolean;
  retryLoad: () => void;
  retrySave: () => void;
};

function PreferenceIllustration({ source }: { source: ImageSourcePropType }) {
  return (
    <Image
      source={source}
      resizeMode="contain"
      style={{ width: ILLUSTRATION_SIZE, height: ILLUSTRATION_SIZE }}
    />
  );
}

function SelectedValue({ children }: { children: string }) {
  return (
    <Text
      className="font-dm-sans"
      numberOfLines={1}
      style={{
        maxWidth: 128,
        fontSize: 13,
        lineHeight: 18,
        fontWeight: "500",
        color: EMBER.colors.textLight,
        textAlign: "right",
      }}
    >
      {children}
    </Text>
  );
}

function PreferenceRow({
  source,
  title,
  subtitle,
  value,
  onPress,
  divider,
  testID,
}: {
  source: ImageSourcePropType;
  title: string;
  subtitle?: string;
  value: string;
  onPress: () => void;
  divider: boolean;
  testID: string;
}) {
  return (
    <Row
      surface="plain"
      leading={<PreferenceIllustration source={source} />}
      leadingSize={ILLUSTRATION_SIZE}
      title={title}
      subtitle={subtitle}
      trailing={<SelectedValue>{value}</SelectedValue>}
      onPress={onPress}
      action="preference_row"
      accessibilityLabel={`${title}: ${value}`}
      gap={14}
      paddingHorizontal={16}
      paddingVertical={13}
      borderRadius={0}
      titleStyle={{
        fontSize: 15,
        lineHeight: 20,
        fontWeight: "500",
        color: EMBER.colors.text,
      }}
      subtitleStyle={{
        fontSize: 12,
        lineHeight: 16,
        color: EMBER.colors.textFaint,
      }}
      style={{
        borderRadius: 0,
        borderTopWidth: divider ? 1 : 0,
        borderTopColor: colors.border,
      }}
      testID={testID}
    />
  );
}

function TogglePreferenceRow({
  source,
  title,
  subtitle,
  value,
  onChange,
  divider,
  testID,
}: {
  source: ImageSourcePropType;
  title: string;
  subtitle: string;
  value: boolean;
  onChange: (next: boolean) => void;
  divider: boolean;
  testID: string;
}) {
  return (
    <Row
      surface="plain"
      leading={<PreferenceIllustration source={source} />}
      leadingSize={ILLUSTRATION_SIZE}
      title={title}
      subtitle={subtitle}
      trailing={
        <MoonToggle
          value={value}
          onChange={onChange}
          accessibilityLabel={title}
          accessibilityHint={subtitle}
          testID={testID}
        />
      }
      chevron={false}
      gap={14}
      paddingHorizontal={16}
      paddingVertical={14}
      borderRadius={0}
      titleStyle={{
        fontSize: 15,
        lineHeight: 20,
        fontWeight: "500",
        color: EMBER.colors.text,
      }}
      subtitleStyle={{
        fontSize: 12,
        lineHeight: 17,
        color: EMBER.colors.textFaint,
      }}
      style={{
        borderRadius: 0,
        borderTopWidth: divider ? 1 : 0,
        borderTopColor: colors.border,
      }}
    />
  );
}

function PreferencePickerDialog({
  visible,
  title,
  options,
  value,
  onClose,
  onSelect,
}: {
  visible: boolean;
  title: string;
  options: { label: string; value: string }[];
  value: string;
  onClose: () => void;
  onSelect: (value: string) => void;
}) {
  const t = useTranslations();

  return (
    <Dialog
      visible={visible}
      onClose={onClose}
      closeButton="with-background"
      containerStyle={{
        width: "88%",
        maxWidth: 340,
        maxHeight: PICKER_MAX_HEIGHT,
      }}
      // This is a dialog above the Preferences dialog. Keep its surface opaque
      // so rows behind it do not visually compete with the choice at hand.
      surfaceStyle={{
        maxHeight: PICKER_MAX_HEIGHT,
        backgroundColor: "#FFFFFF",
      }}
      contentStyle={{
        paddingTop: 32,
        paddingHorizontal: 16,
        paddingBottom: 16,
      }}
    >
      <Text
        className="font-dm-sans"
        style={{
          paddingRight: 40,
          paddingHorizontal: 8,
          paddingBottom: 14,
          fontSize: 17,
          lineHeight: 22,
          fontWeight: "600",
          color: EMBER.colors.text,
        }}
      >
        {t("you.preferences.picker_choose", { params: { setting: title } })}
      </Text>

      <ScrollView
        style={{
          flexGrow: 0,
          flexShrink: 1,
          maxHeight: PICKER_LIST_MAX_HEIGHT,
        }}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{ paddingBottom: 2 }}
      >
        {options.map((option, index) => {
          const selected = option.value === value;
          return (
            <Row
              key={option.value}
              surface="plain"
              title={option.label}
              trailing={
                selected ? (
                  <Check size={18} strokeWidth={2.2} color={colors.deepBrown} />
                ) : undefined
              }
              action="preference_picker_option"
              onPress={() => {
                onSelect(option.value);
                onClose();
              }}
              chevron={false}
              accessibilityLabel={option.label}
              accessibilityState={{ selected }}
              paddingHorizontal={8}
              paddingVertical={13}
              borderRadius={0}
              titleStyle={{
                fontSize: 15,
                lineHeight: 20,
                fontWeight: selected ? "600" : "500",
                color: EMBER.colors.text,
              }}
              style={{
                borderRadius: 0,
                borderTopWidth: index === 0 ? 0 : 1,
                borderTopColor: colors.border,
              }}
            />
          );
        })}
      </ScrollView>
    </Dialog>
  );
}

function LocationChoiceRow({
  city,
  country,
  selected,
  loading,
  onPress,
  divider,
}: {
  city: string;
  country: string;
  selected: boolean;
  loading?: boolean;
  onPress: () => void;
  divider: boolean;
}) {
  return (
    <Row
      surface="plain"
      leading={<MapPin size={18} strokeWidth={1.8} color={colors.textLight} />}
      leadingSize={24}
      title={city}
      subtitle={country}
      trailing={
        loading ? (
          <ActivityIndicator size="small" color={colors.warm} />
        ) : selected ? (
          <Check size={18} strokeWidth={2.2} color={colors.deepBrown} />
        ) : undefined
      }
      onPress={loading ? undefined : onPress}
      action="location_choice_row"
      chevron={false}
      accessibilityLabel={`${city}, ${country}`}
      accessibilityState={{ selected, busy: Boolean(loading) }}
      paddingHorizontal={8}
      paddingVertical={12}
      borderRadius={0}
      titleStyle={{
        fontSize: 15,
        lineHeight: 20,
        fontWeight: selected ? "600" : "500",
        color: EMBER.colors.text,
      }}
      subtitleStyle={{
        fontSize: 12,
        lineHeight: 16,
        color: EMBER.colors.textFaint,
      }}
      style={{
        borderRadius: 0,
        borderTopWidth: divider ? 1 : 0,
        borderTopColor: colors.border,
      }}
    />
  );
}

/**
 * Location is the patient-facing input. We persist its city label alongside
 * the canonical IANA timezone so the row remains truthful for cities that
 * share a timezone (for example, San Francisco and Los Angeles).
 * A country alone can span multiple timezones, so this asks the person to pick
 * a city result and resolves that location to a canonical IANA timezone.
 */
function LocationTimezonePickerDialog({
  visible,
  value,
  onClose,
  onSelect,
  onChooseManual,
}: {
  visible: boolean;
  value: string;
  onClose: () => void;
  onSelect: (location: LocationSuggestion) => void;
  onChooseManual: () => void;
}) {
  const t = useTranslations();
  const [searchText, setSearchText] = useState("");
  const [selectingPlaceId, setSelectingPlaceId] = useState<string | null>(null);
  const [selectionError, setSelectionError] = useState<string | null>(null);
  const hasSearch = searchText.trim().length >= 2;
  // Cities only: a country can span multiple timezones, so a country-level
  // pick can't resolve to a single IANA zone.
  const { predictions, isSearching } = usePlacesPredictions({
    query: searchText,
    minChars: 2,
    debounceMs: 250,
    citiesOnly: true,
    enabled: visible,
  });

  // Reset the caller-owned selection state when the dialog closes; predictions
  // and the searching flag idle automatically via the hook's `enabled`.
  useEffect(() => {
    if (!visible) {
      setSearchText("");
      setSelectingPlaceId(null);
      setSelectionError(null);
    }
  }, [visible]);

  const selectPlace = async (prediction: PlacePrediction) => {
    if (selectingPlaceId) return;

    setSelectingPlaceId(prediction.placeId);
    setSelectionError(null);

    try {
      const details = await getPlaceDetails(prediction.placeId);
      const location = details ? extractCityAndCountry(details) : null;
      const timezone = details
        ? resolveTimezoneFromCoordinates({
            latitude: details.geometry.location.lat,
            longitude: details.geometry.location.lng,
          })
        : null;

      // Do not guess from a country-level result. Multi-zone countries need a
      // city (or the explicit manual fallback) to keep schedules correct.
      if (!location || !timezone) {
        throw new Error("No location timezone available");
      }

      onSelect({
        city: location.city,
        country: location.country,
        timezone,
      });
      onClose();
    } catch {
      setSelectionError(t("you.preferences.location_lookup_failed"));
    } finally {
      setSelectingPlaceId(null);
    }
  };

  const renderPredictions = () => {
    if (!hasSearch) {
      return (
        <>
          <Text
            className="font-dm-sans"
            style={{
              paddingHorizontal: 8,
              paddingBottom: 6,
              fontSize: 12,
              lineHeight: 16,
              fontWeight: "500",
              color: EMBER.colors.textFaint,
            }}
          >
            {t("you.preferences.location_suggested")}
          </Text>
          {LOCATION_SUGGESTIONS.map((location, index) => (
            <LocationChoiceRow
              key={location.timezone}
              city={location.city}
              country={location.country}
              selected={location.timezone === value}
              divider={index !== 0}
              onPress={() => {
                onSelect(location);
                onClose();
              }}
            />
          ))}
        </>
      );
    }

    if (isSearching) {
      return (
        <View
          style={{
            minHeight: 110,
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <ActivityIndicator size="small" color={colors.warm} />
        </View>
      );
    }

    if (predictions.length === 0) {
      return (
        <Text
          className="font-dm-sans"
          style={{
            paddingHorizontal: 8,
            paddingVertical: 20,
            fontSize: 13,
            lineHeight: 19,
            textAlign: "center",
            color: EMBER.colors.textFaint,
          }}
        >
          {t("you.preferences.location_no_matches")}
        </Text>
      );
    }

    return predictions.map((prediction, index) => (
      <LocationChoiceRow
        key={prediction.placeId}
        city={prediction.structuredFormatting.mainText}
        country={prediction.structuredFormatting.secondaryText}
        selected={false}
        loading={selectingPlaceId === prediction.placeId}
        divider={index !== 0}
        onPress={() => void selectPlace(prediction)}
      />
    ));
  };

  return (
    <Dialog
      visible={visible}
      onClose={onClose}
      closeButton="with-background"
      containerStyle={{
        width: "88%",
        maxWidth: 340,
        maxHeight: LOCATION_PICKER_MAX_HEIGHT,
      }}
      surfaceStyle={{
        maxHeight: LOCATION_PICKER_MAX_HEIGHT,
        backgroundColor: "#FFFFFF",
      }}
      contentStyle={{
        paddingTop: 32,
        paddingHorizontal: 16,
        paddingBottom: 16,
      }}
    >
      <Text
        className="font-dm-sans"
        style={{
          paddingRight: 40,
          paddingHorizontal: 8,
          paddingBottom: 4,
          fontSize: 17,
          lineHeight: 22,
          fontWeight: "600",
          color: EMBER.colors.text,
        }}
      >
        {t("you.preferences.location_picker_title")}
      </Text>
      <Text
        className="font-dm-sans"
        style={{
          paddingHorizontal: 8,
          paddingBottom: 14,
          fontSize: 12,
          lineHeight: 17,
          color: EMBER.colors.textFaint,
        }}
      >
        {t("you.preferences.location_picker_description")}
      </Text>

      <MoonSearchInput
        value={searchText}
        onChangeText={(next) => {
          setSearchText(next);
          setSelectionError(null);
        }}
        onClear={() => {
          setSearchText("");
          setSelectionError(null);
        }}
        placeholder={t("you.preferences.location_search_placeholder")}
        accessibilityLabel={t("you.preferences.location_search_placeholder")}
        autoCapitalize="words"
      />

      {selectionError ? (
        <Text
          className="font-dm-sans"
          style={{
            paddingHorizontal: 8,
            paddingTop: 10,
            fontSize: 12,
            lineHeight: 16,
            color: colors.coral,
          }}
        >
          {selectionError}
        </Text>
      ) : null}

      <ScrollView
        style={{
          flexGrow: 0,
          flexShrink: 1,
          maxHeight: LOCATION_PICKER_LIST_MAX_HEIGHT,
          marginTop: 14,
        }}
        contentContainerStyle={{ paddingBottom: 2 }}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
      >
        {renderPredictions()}
      </ScrollView>

      <View
        style={{
          borderTopWidth: 1,
          borderTopColor: colors.border,
          marginTop: 8,
          paddingTop: 2,
        }}
      >
        <Row
          surface="plain"
          leading={
            <Clock3 size={18} strokeWidth={1.8} color={colors.textLight} />
          }
          leadingSize={24}
          title={t("you.preferences.manual_timezone_title")}
          subtitle={t("you.preferences.manual_timezone_description")}
          onPress={onChooseManual}
          action="you.preferences.manual_timezone_title"
          chevron={false}
          accessibilityLabel={t("you.preferences.manual_timezone_title")}
          paddingHorizontal={8}
          paddingVertical={12}
          borderRadius={0}
          titleStyle={{
            fontSize: 14,
            lineHeight: 19,
            fontWeight: "500",
            color: EMBER.colors.text,
          }}
          subtitleStyle={{
            fontSize: 12,
            lineHeight: 16,
            color: EMBER.colors.textFaint,
          }}
          style={{ borderRadius: 0 }}
        />
      </View>
    </Dialog>
  );
}

function locationLabelForTimezone(timezone: string) {
  const suggestion = LOCATION_SUGGESTIONS.find(
    (location) => location.timezone === timezone,
  );
  if (suggestion) return suggestion.city;

  const canonicalCity = timezone.split("/").at(-1)?.replaceAll("_", " ");
  return canonicalCity || timezone;
}

type Props = {
  preferences: UserPreferences;
  status: PreferencesStatus;
  dispatch: React.Dispatch<YouScreenAction>;
};

/**
 * The surrounding detail dialog owns the Preferences title. Keep this content
 * to canonical rows and compact choice dialogs so it stays easy to use on
 * both desktop and mobile instead of expanding settings inline.
 */
export default function Preferences({ preferences, status, dispatch }: Props) {
  const t = useTranslations();
  const [picker, setPicker] = useState<PickerId | null>(null);

  const languageOptions = LANGUAGE_OPTION_KEYS.map((option) => ({
    label: t(option.labelKey),
    value: option.value,
  }));
  const timezoneOptions = TIMEZONE_OPTION_KEYS.map((option) => ({
    label: t(option.labelKey),
    value: option.value,
  }));

  const languageLabel =
    languageOptions.find((option) => option.value === preferences.language)
      ?.label ?? preferences.language;
  const locationLabel =
    preferences.timezoneLocation?.trim() ||
    locationLabelForTimezone(preferences.timezone);

  const selectLocation = (location: LocationSuggestion) => {
    dispatch({
      type: "SET_TIMEZONE",
      timezone: location.timezone,
      timezoneLocation: location.city,
    });
  };

  const selectTimezoneManually = (timezone: string) => {
    dispatch({ type: "SET_TIMEZONE", timezone, timezoneLocation: null });
  };

  return (
    <View>
      <GroupedCard style={{ marginBottom: 12 }}>
        <PreferenceRow
          source={PREFERENCE_ICONS.language}
          title={t("you.preferences.language_label")}
          value={languageLabel}
          divider={false}
          testID="preferences-language-row"
          onPress={() => setPicker("language")}
        />
        <PreferenceRow
          source={PREFERENCE_ICONS.dateFormat}
          title={t("you.preferences.date_format_label")}
          value={preferences.dateFormat}
          divider
          testID="preferences-date-format-row"
          onPress={() => setPicker("date-format")}
        />
        <PreferenceRow
          source={PREFERENCE_ICONS.timezone}
          title={t("you.preferences.timezone_label")}
          subtitle={t("you.preferences.timezone_description")}
          value={locationLabel}
          divider
          testID="preferences-timezone-row"
          onPress={() => setPicker("timezone-location")}
        />
        <TogglePreferenceRow
          source={PREFERENCE_ICONS.haptics}
          title={t("you.preferences.haptics_label")}
          subtitle={t("you.preferences.haptics_description")}
          value={preferences.hapticsEnabled}
          onChange={(value) => dispatch({ type: "SET_HAPTICS", value })}
          divider
          testID="preferences-haptics-toggle"
        />
        <TogglePreferenceRow
          source={PREFERENCE_ICONS.sound}
          title={t("you.preferences.sound_label")}
          subtitle={t("you.preferences.sound_description")}
          value={preferences.soundEnabled}
          onChange={(value) => dispatch({ type: "SET_SOUND", value })}
          divider
          testID="preferences-sound-toggle"
        />
      </GroupedCard>

      {status.hasLoadError ? (
        <ActionBanner
          variant="important"
          message={t("you.preferences.preferences_load_failed")}
          actionLabel={t("you.preferences.try_again")}
          onAction={status.retryLoad}
        />
      ) : null}
      {status.hasSaveError ? (
        <View style={{ marginTop: status.hasLoadError ? 10 : 0 }}>
          <ActionBanner
            variant="important"
            message={t("you.preferences.preferences_save_failed")}
            actionLabel={t("you.preferences.try_again")}
            onAction={status.retrySave}
          />
        </View>
      ) : null}

      <PreferencePickerDialog
        visible={picker === "language"}
        title={t("you.preferences.language_label")}
        options={languageOptions}
        value={preferences.language}
        onClose={() => setPicker(null)}
        onSelect={(language) =>
          dispatch({ type: "SET_LANGUAGE", language: language as Language })
        }
      />
      <PreferencePickerDialog
        visible={picker === "date-format"}
        title={t("you.preferences.date_format_label")}
        options={DATE_FORMAT_OPTIONS}
        value={preferences.dateFormat}
        onClose={() => setPicker(null)}
        onSelect={(format) =>
          dispatch({ type: "SET_DATE_FORMAT", format: format as DateFormat })
        }
      />
      <LocationTimezonePickerDialog
        visible={picker === "timezone-location"}
        value={preferences.timezone}
        onClose={() => setPicker(null)}
        onSelect={selectLocation}
        onChooseManual={() => setPicker("timezone-manual")}
      />
      <PreferencePickerDialog
        visible={picker === "timezone-manual"}
        title={t("you.preferences.manual_timezone_picker_title")}
        options={timezoneOptions}
        value={preferences.timezone}
        onClose={() => setPicker(null)}
        onSelect={selectTimezoneManually}
      />
    </View>
  );
}
